import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  LayoutDashboard,
  Route as RouteIcon,
  Map,
  History,
  User,
  Phone,
  MessageCircle,
  Clock,
  MapPin,
  Info,
  Navigation,
  AlertTriangle,
  Star,
} from "lucide-react";
import PanelShell from "../../components/PanelShell";
import RouteMap from "@/components/maps/RouteMap";
import { useAuth } from "@/context/AuthContext";
import { driverApi } from "@/lib/driverApi";

const navItems = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta optimizada", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Mis rutas", icon: Map },
  { to: "/conductor/historial", label: "Historial", icon: History },
  { to: "/conductor/calificaciones", label: "Calificaciones", icon: Star },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Rutas", icon: Map },
  { to: "/conductor/historial", label: "Viajes", icon: History },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];

const formatEta = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
};

// Recorre las rutas compartidas del conductor y junta las salidas con al
// menos un pasajero -- reusa endpoints que ya existían, no hace falta uno
// nuevo solo para "listar salidas con reservas".
const useUpcomingDepartures = () => {
  const [departures, setDepartures] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: routes } = await driverApi.listRoutes();
      const sharedRoutes = routes.filter((r) => r.shared_enabled);
      const perRoute = await Promise.all(
        sharedRoutes.map(async (route) => {
          const { data: routeDepartures } = await driverApi.listDepartures(route.id);
          return routeDepartures
            .filter((d) => d.taken_seats.length > 0 && d.status === "programada")
            .map((d) => ({ ...d, route }));
        })
      );
      if (cancelled) return;
      const flat = perRoute.flat().sort((a, b) => `${a.departure_date}${a.departure_time}`.localeCompare(`${b.departure_date}${b.departure_time}`));
      setDepartures(flat);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return departures;
};

const DeparturePicker = () => {
  const departures = useUpcomingDepartures();
  const navigate = useNavigate();

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
      <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display flex items-center gap-2 mb-2">
        <Navigation size={12} /> Ruta optimizada
      </p>
      <h1 className="font-display font-bold text-2xl uppercase tracking-tight mb-6">Elige una salida</h1>

      {departures === null && <p className="text-sm text-[#A0A0A5] font-body">Cargando salidas…</p>}
      {departures !== null && departures.length === 0 && (
        <p className="text-sm text-[#A0A0A5] font-body">No tienes salidas compartidas con pasajeros todavía.</p>
      )}
      <div className="space-y-3">
        {departures?.map((d) => (
          <button
            key={d.id}
            onClick={() => navigate(`/conductor/ruta?departureId=${d.id}`)}
            className="w-full text-left bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227] rounded-xl p-5 flex items-center justify-between transition"
            data-testid={`departure-option-${d.id}`}
          >
            <div>
              <p className="font-display font-semibold text-sm">
                {d.route.airport_name} → {d.route.city}
              </p>
              <p className="text-xs text-[#A0A0A5] mt-1">
                {d.departure_date} · {d.departure_time.slice(0, 5)}
              </p>
            </div>
            <span className="text-[10px] uppercase tracking-wider text-[#C9A227] font-display">
              {d.taken_seats.length} pasajero{d.taken_seats.length === 1 ? "" : "s"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const DriverRoute = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const departureId = searchParams.get("departureId");

  const [routeData, setRouteData] = useState(null);
  const [fallbackStops, setFallbackStops] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!departureId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setRouteData(null);
    setFallbackStops(null);

    driverApi
      .getDepartureRoute(departureId)
      .then(({ data }) => {
        if (!cancelled) setRouteData(data);
      })
      .catch(async (err) => {
        if (cancelled) return;
        if (err.response?.status === 503) {
          // Maps no configurado -- fallback a la lista simple sin
          // optimizar, sin mapa (CLAUDE.md 2c). Reusa la lista de
          // reservas del conductor en vez de romper la pantalla.
          const { data: reservations } = await driverApi.listReservations();
          setFallbackStops(reservations.filter((r) => r.departure_id === departureId && r.status !== "cancelado"));
        } else if (err.response?.status === 404) {
          setError("Salida no encontrada.");
        } else {
          setError("No se pudo cargar la ruta. Intenta de nuevo.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [departureId]);

  if (!departureId) {
    return (
      <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-route-page">
        <DeparturePicker />
      </PanelShell>
    );
  }

  if (loading) {
    return (
      <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-route-page">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12 text-sm text-[#A0A0A5] font-body">Calculando ruta…</div>
      </PanelShell>
    );
  }

  if (error) {
    return (
      <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-route-page">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-12 text-sm text-red-400 font-body">{error}</div>
      </PanelShell>
    );
  }

  // Fallback sin Maps configurado: lista simple, sin mapa ni orden
  // optimizado -- nunca pantalla en blanco.
  if (fallbackStops) {
    return (
      <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-route-page">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
          <div className="mb-6 flex items-center gap-3 px-4 py-3 border border-[#C9A227]/40 rounded-lg bg-[#C9A227]/5" data-testid="maps-unavailable-banner">
            <AlertTriangle size={16} className="text-[#C9A227] shrink-0" />
            <p className="text-xs text-[#A0A0A5] font-body">
              Google Maps no está configurado todavía -- mostrando la lista de pasajeros sin ruta optimizada.
            </p>
          </div>
          <ul className="divide-y divide-[#2E2F33] bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden">
            {fallbackStops.map((r) => (
              <li key={r.id} className="p-5" data-testid={`fallback-stop-${r.id}`}>
                <p className="font-display font-semibold text-sm">{r.contact_name}</p>
                <p className="text-xs text-[#A0A0A5] font-body mt-1 flex items-start gap-1">
                  <MapPin size={11} className="text-[#C9A227] mt-0.5 shrink-0" /> {r.passenger_point_address}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </PanelShell>
    );
  }

  const stops = routeData.stops;
  const roleLabel = routeData.passenger_point_role === "destino" ? "Destinos" : "Recojos";

  return (
    <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-route-page">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display flex items-center gap-2">
              <Navigation size={12} /> Ruta optimizada · {roleLabel}
            </p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">
              {stops.length} parada{stops.length === 1 ? "" : "s"} · orden óptimo
            </h1>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">Tiempo total</p>
              <p className="font-display font-bold text-xl text-[#C9A227]">
                {routeData.total_duration_minutes != null ? `${Math.round(routeData.total_duration_minutes)} min` : "—"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">Distancia</p>
              <p className="font-display font-bold text-xl text-[#F5F5F5]">
                {routeData.total_distance_km != null ? `${routeData.total_distance_km.toFixed(1)} km` : "—"}
              </p>
            </div>
          </div>
        </div>

        {stops.length === 0 ? (
          <p className="text-sm text-[#A0A0A5] font-body" data-testid="no-stops-message">
            Esta salida todavía no tiene pasajeros reservados.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
            <div className="relative min-h-[360px]" data-testid="driver-route-map">
              <RouteMap stops={stops} />
            </div>

            <aside className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden" data-testid="stops-panel">
              <div className="p-5 border-b border-[#2E2F33] flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Paradas</p>
                  <h3 className="font-display font-bold text-lg uppercase tracking-wide">Orden óptimo</h3>
                </div>
                <div className="flex items-center gap-2 px-2.5 py-1 border border-[#C9A227]/40 rounded-full">
                  <Info size={11} className="text-[#C9A227]" />
                  <span className="text-[9px] uppercase tracking-wider text-[#C9A227] font-display">Directions API</span>
                </div>
              </div>

              <ul className="divide-y divide-[#2E2F33] max-h-[500px] overflow-y-auto">
                {stops.map((s) => (
                  <li key={s.reservation_id} className="p-5" data-testid={`stop-${s.order}`}>
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-[#C9A227] flex items-center justify-center font-display font-bold text-[#0D0E10]">
                          {s.order}
                        </div>
                        {s.order < stops.length && (
                          <div className="absolute left-1/2 top-full w-px h-8 -translate-x-1/2 bg-gradient-to-b from-[#C9A227]/40 to-transparent" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-display font-semibold text-sm truncate">{s.contact_name}</p>
                          <span className="text-[10px] uppercase tracking-wider text-[#C9A227] font-display shrink-0">
                            ETA {formatEta(s.eta)}
                          </span>
                        </div>
                        <p className="text-xs text-[#A0A0A5] font-body mt-1 flex items-start gap-1">
                          <MapPin size={11} className="text-[#C9A227] mt-0.5 shrink-0" /> {s.passenger_point_address}
                        </p>
                        {s.distance_from_previous_km != null && (
                          <div className="mt-2.5 flex items-center gap-2 text-[10px] text-[#A0A0A5]">
                            <Clock size={10} className="text-[#C9A227]" /> {s.distance_from_previous_km.toFixed(1)} km ·{" "}
                            {Math.round(s.duration_from_previous_minutes)} min desde la parada anterior
                          </div>
                        )}
                        <div className="mt-3 flex items-center gap-2">
                          <a
                            href={`tel:${s.contact_phone}`}
                            className="p-2 border border-[#2E2F33] hover:border-[#C9A227] hover:text-[#C9A227] rounded-lg text-[#A0A0A5] transition"
                            aria-label="Llamar"
                            data-testid={`call-${s.order}`}
                          >
                            <Phone size={12} />
                          </a>
                          <a
                            href={`https://wa.me/${s.contact_phone.replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-2 border border-[#2E2F33] hover:border-[#C9A227] hover:text-[#C9A227] rounded-lg text-[#A0A0A5] transition"
                            aria-label="WhatsApp"
                            data-testid={`whatsapp-${s.order}`}
                          >
                            <MessageCircle size={12} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        )}
      </div>
    </PanelShell>
  );
};

export default DriverRoute;
